import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import ProductDetailModal from './components/ProductDetailModal';
import {
  ShieldCheck, Zap, Headphones, Smartphone,
  Package, MapPin, Phone, Mail, Clock,
  MessageCircle, ChevronRight, Check, Home
} from 'lucide-react';

// ============== 数据 ==============
const banners = [
  {
    title: '专业守护，平安相伴',
    subtitle: '新疆平安保险，为您提供全方位的保险保障方案',
    cta: '立即咨询',
    bg: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&h=600&fit=crop',
  },
  {
    title: '智能保险，贴心服务',
    subtitle: '科技赋能保险服务，让保障更简单、更温暖',
    cta: '了解产品',
    bg: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1920&h=600&fit=crop',
  },
  {
    title: '全家保障，一站解决',
    subtitle: '车险、寿险、健康险、财产险，满足您全家的保障需求',
    cta: '免费测算',
    bg: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920&h=600&fit=crop',
  },
];

const advantages = [
  {
    Icon: ShieldCheck,
    title: '专业可靠',
    desc: '深耕保险行业多年，拥有丰富的风险管理经验和专业的服务团队',
  },
  {
    Icon: Zap,
    title: '快速理赔',
    desc: '线上报案，智能审核，最快30分钟完成理赔，让您无后顾之忧',
  },
  {
    Icon: Headphones,
    title: '7x24服务',
    desc: '全天候客服热线，随时为您解答疑问，提供专业保险咨询服务',
  },
  {
    Icon: Smartphone,
    title: '线上便捷',
    desc: '支持在线投保、续保、理赔，随时随地管理您的保单',
  },
];

const products = [
  {
    id: 1,
    name: '平安车险',
    tagline: '全方位车辆保障，道路救援7x24小时',
    image: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=600&h=450&fit=crop',
  },
  {
    id: 2,
    name: '平安健康险',
    tagline: '覆盖百种重疾，就医绿通尊享服务',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=450&fit=crop',
  },
  {
    id: 3,
    name: '平安家财险',
    tagline: '守护您的温馨家园，火灾水损全面保障',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=450&fit=crop',
  },
  {
    id: 4,
    name: '平安意外险',
    tagline: '意外无处不在，保障时刻相随',
    image: 'https://images.unsplash.com/photo-1517649763962-0c623066013b?w=600&h=450&fit=crop',
  },
  {
    id: 5,
    name: '平安寿险',
    tagline: '为爱留一份保障，守护家人未来',
    image: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=600&h=450&fit=crop',
  },
  {
    id: 6,
    name: '平安旅游险',
    tagline: '境内外旅游全覆盖，紧急救援随时达',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=450&fit=crop',
  },
];

const aboutImages = [
  'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=450&fit=crop',
  'https://images.unsplash.com/photo-1524758631624-e2822e304c36?w=300&h=200&fit=crop',
  'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=300&h=200&fit=crop',
];

const contactInfo = [
  { Icon: MapPin, label: '公司地址', value: '新疆维吾尔自治区乌鲁木齐市天山区光明路XXX号平安大厦' },
  { Icon: Phone, label: '服务热线', value: '0991-XXXXXXX' },
  { Icon: Mail, label: '电子邮箱', value: 'service@xjpingan.com' },
  { Icon: Clock, label: '服务时间', value: '周一至周日 8:30 - 20:00' },
];

// ============== 滚动动画 Hook ==============
function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

function ScrollReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, visible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `all 600ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

// ============== 新导航栏组件 ==============
function PingAnNavbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.98)' : '#fff',
        boxShadow: scrolled ? '0 2px 12px rgba(0,0,0,0.1)' : '0 1px 4px rgba(0,0,0,0.05)',
      }}
    >
      {/* 顶部条 - 参考平安官网风格 */}
      <div className="bg-gray-100 text-xs text-gray-600">
        <div className="max-w-7xl mx-auto px-4 h-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span>中国平安旗下网站</span>
            <span className="text-gray-400">|</span>
            <span>专业 让生活更简单</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="cursor-pointer hover:text-orange-500 transition-colors">联系我们</span>
            <span className="text-gray-400">|</span>
            <span className="cursor-pointer hover:text-orange-500 transition-colors">网站地图</span>
            <span className="text-gray-400">|</span>
            <span className="cursor-pointer hover:text-orange-500 transition-colors">中文繁体</span>
            <span className="text-gray-400">|</span>
            <span className="cursor-pointer hover:text-orange-500 transition-colors">English</span>
          </div>
        </div>
      </div>

      {/* 主导航 */}
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo 区域 */}
        <div className="flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3 text-decoration-none">
            <div className="flex flex-col">
              <div className="flex items-center gap-1">
                <span className="text-xl font-bold" style={{ color: '#FF6B3D' }}>中国平安</span>
                <span className="text-lg font-semibold text-gray-700">PING AN</span>
              </div>
              <span className="text-xs text-gray-500 tracking-wider">新疆地区官方网站</span>
            </div>
          </Link>
        </div>

        {/* 导航链接 - 只保留首页 */}
        <nav className="flex items-center gap-8">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-sm font-medium transition-colors hover:text-orange-500"
            style={{ color: '#FF6B3D' }}
          >
            <Home className="w-4 h-4" />
            首页
          </Link>
          
          {/* 其他静态展示链接（不可点击，仅展示） */}
          <span className="text-sm text-gray-400 cursor-default">个人客户</span>
          <span className="text-sm text-gray-400 cursor-default">企业客户</span>
          <span className="text-sm text-gray-400 cursor-default">网上商城</span>
          <span className="text-sm text-gray-400 cursor-default">一账通</span>
        </nav>

        {/* 右侧服务入口 */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Phone className="w-4 h-4" style={{ color: '#FF6B3D' }} />
            <span className="font-semibold" style={{ color: '#FF6B3D' }}>95511</span>
          </div>
          <div 
            className="px-4 py-2 rounded text-sm text-white font-medium cursor-pointer transition-all hover:shadow-md"
            style={{ backgroundColor: '#FF6B3D' }}
          >
            平安VIP俱乐部
          </div>
        </div>
      </div>

      {/* 橙色分隔条 */}
      <div className="h-1" style={{ backgroundColor: '#FF6B3D' }} />
    </header>
  );
}

// ============== 页脚组件 ==============
function PingAnFooter() {
  return (
    <footer className="bg-gray-100 pt-12 pb-6">
      <div className="max-w-7xl mx-auto px-4">
        {/* 主要内容区 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Logo 和热线 */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl font-bold" style={{ color: '#FF6B3D' }}>中国平安</span>
              <span className="text-sm font-semibold text-gray-600">PING AN</span>
            </div>
            <p className="text-sm text-gray-500 mb-2">专业 让生活更简单</p>
            <div className="flex items-center gap-2 mt-4">
              <Phone className="w-5 h-5" style={{ color: '#FF6B3D' }} />
              <span className="text-lg font-bold" style={{ color: '#FF6B3D' }}>服务热线 95511</span>
            </div>
          </div>

          {/* 关于平安 */}
          <div>
            <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
              <Home className="w-4 h-4" />
              关于平安
            </h4>
            <ul className="space-y-2">
              {['集团介绍', '投资者关系', '加盟平安', '联系我们'].map((item) => (
                <li key={item}>
                  <span className="text-sm text-gray-500 hover:text-orange-500 cursor-pointer transition-colors">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* 企业社会责任 */}
          <div>
            <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              企业社会责任
            </h4>
            <ul className="space-y-2">
              {['企业社会责任报告', '低碳100', '励志计划', '支教行动'].map((item) => (
                <li key={item}>
                  <span className="text-sm text-gray-500 hover:text-orange-500 cursor-pointer transition-colors">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* 帮助中心 */}
          <div>
            <h4 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              帮助中心
            </h4>
            <ul className="space-y-2">
              {['隐私保密声明', '网站声明', '链接说明', '网站地图'].map((item) => (
                <li key={item}>
                  <span className="text-sm text-gray-500 hover:text-orange-500 cursor-pointer transition-colors">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4">
              <p className="text-sm text-gray-500 mb-2">移动平安网</p>
              <p className="text-sm text-gray-700 font-medium">m.pingan.com</p>
              {/* 二维码占位 */}
              <div className="w-24 h-24 bg-white border border-gray-200 rounded mt-2 flex items-center justify-center">
                <span className="text-xs text-gray-400">二维码</span>
              </div>
            </div>
          </div>
        </div>

        {/* 分隔线 */}
        <div className="border-t border-gray-200 pt-6">
          <p className="text-xs text-gray-500 text-center mb-2">
            版权所有 © 中国平安保险（集团）股份有限公司 未经许可不得复制、转载或摘编，违者必究！
          </p>
          <p className="text-xs text-gray-400 text-center mb-2">
            Copyright © PING AN INSURANCE (GROUP) COMPANY OF CHINA, LTD. All Rights Reserved
          </p>
          <div className="flex items-center justify-center gap-4 mt-2">
            <span className="text-xs text-gray-400">ICP许可证号 粤ICP备06118290号</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============== 主组件 ==============

export default function XinjiangPingAn() {
  const [currentBanner, setCurrentBanner] = useState(0);
  const [modalProduct, setModalProduct] = useState<typeof products[0] | null>(null);
  const [formData, setFormData] = useState({ name: '', phone: '', insuranceType: '', message: '' });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const contactRef = useRef<HTMLDivElement>(null);

  // Banner 自动轮播
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBanner((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // 打开模态框时禁止滚动
  useEffect(() => {
    document.body.style.overflow = modalProduct ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [modalProduct]);

  const scrollToContact = useCallback(() => {
    contactRef.current?.scrollIntoView({ behavior: 'smooth' });
    setModalProduct(null);
  }, []);

  const handleBannerCta = useCallback(() => {
    contactRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleFormChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = '请输入姓名';
    if (!formData.phone.trim()) {
      errors.phone = '请输入手机号';
    } else if (!/^1[3-9]\d{9}$/.test(formData.phone.trim())) {
      errors.phone = '请输入正确的手机号';
    }
    if (!formData.insuranceType) errors.insuranceType = '请选择意向险种';
    if (!formData.message.trim()) {
      errors.message = '请输入留言内容';
    } else if (formData.message.trim().length < 10) {
      errors.message = '留言内容至少10个字';
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1500));
    setSubmitting(false);
    setShowSuccess(true);
    setFormData({ name: '', phone: '', insuranceType: '', message: '' });
    setTimeout(() => setShowSuccess(false), 5000);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* 新导航栏 */}
      <PingAnNavbar />

      {/* 占位，防止导航栏遮挡内容 */}
      <div className="h-[97px]" />

      {/* ========== Section 1: Hero Banner 轮播 ========== */}
      <section className="relative h-[500px] md:h-[600px] overflow-hidden">
        {banners.map((b, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-700"
            style={{ opacity: i === currentBanner ? 1 : 0 }}
          >
            <img src={b.bg} alt={b.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/40" />
          </div>
        ))}

        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="max-w-3xl mx-auto text-center px-4">
            <h1
              key={`title-${currentBanner}`}
              className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg"
              style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}
            >
              {banners[currentBanner].title}
            </h1>
            <p className="text-lg md:text-xl text-white/80 mt-4">
              {banners[currentBanner].subtitle}
            </p>
            <button
              onClick={handleBannerCta}
              className="mt-8 px-8 py-3 rounded-lg text-white font-semibold transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
              style={{ backgroundColor: '#FF6B3D' }}
              onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#E55A2B'; }}
              onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = '#FF6B3D'; }}
            >
              {banners[currentBanner].cta}
            </button>
          </div>
        </div>

        {/* 指示器 */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentBanner(i)}
              className="w-3 h-3 rounded-full transition-all duration-300"
              style={{
                backgroundColor: i === currentBanner ? '#FF6B3D' : 'rgba(255,255,255,0.5)',
                transform: i === currentBanner ? 'scale(1.2)' : 'scale(1)',
              }}
            />
          ))}
        </div>
      </section>

      {/* ========== Section 2: 核心优势 ========== */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: '#FFF5F2', color: '#FF6B3D' }}>
              <ShieldCheck className="w-4 h-4" />
              核心优势
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4" style={{ color: '#1A1A2E' }}>
              为什么选择新疆平安保险
            </h2>
            <p className="mt-3 max-w-2xl mx-auto" style={{ color: '#6B7280' }}>
              我们致力于为每一位客户提供专业、高效、贴心的保险服务体验
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {advantages.map((a, i) => (
              <ScrollReveal key={i} delay={i * 100}>
                <div className="group p-6 rounded-xl border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 text-center">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto"
                    style={{ backgroundColor: '#FFF5F2' }}
                  >
                    <a.Icon className="w-7 h-7" style={{ color: '#FF6B3D' }} />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold" style={{ color: '#1A1A2E' }}>{a.title}</h3>
                  <p className="mt-2 text-sm" style={{ color: '#6B7280' }}>{a.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ========== Section 3: 热门产品 ========== */}
      <section style={{ backgroundColor: '#F7F8FA' }} className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: '#FFF5F2', color: '#FF6B3D' }}>
              <Package className="w-4 h-4" />
              热门产品
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4" style={{ color: '#1A1A2E' }}>
              为您精选的保险产品
            </h2>
            <p className="mt-3 max-w-2xl mx-auto" style={{ color: '#6B7280' }}>
              覆盖车险、健康险、家财险、意外险等多个领域，满足您全家的保障需求
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {products.map((p, i) => (
              <ScrollReveal key={p.id} delay={i * 100}>
                <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group">
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-semibold" style={{ color: '#1A1A2E' }}>{p.name}</h3>
                    <p className="mt-2 text-sm line-clamp-2" style={{ color: '#6B7280' }}>{p.tagline}</p>
                    <button
                      onClick={() => setModalProduct(p)}
                      className="mt-4 w-full py-2.5 rounded-lg font-medium border transition-all duration-300"
                      style={{
                        borderColor: '#FF6B3D',
                        color: '#FF6B3D',
                      }}
                      onMouseEnter={(e) => {
                        const t = e.currentTarget;
                        t.style.backgroundColor = '#FF6B3D';
                        t.style.color = '#fff';
                      }}
                      onMouseLeave={(e) => {
                        const t = e.currentTarget;
                        t.style.backgroundColor = 'transparent';
                        t.style.color = '#FF6B3D';
                      }}
                    >
                      了解详情
                    </button>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ========== Section 4: 关于我们 ========== */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: '#FFF5F2', color: '#FF6B3D' }}>
              <ShieldCheck className="w-4 h-4" />
              关于我们
            </span>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mt-12">
              {/* 左栏 - 文字 */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1A1A2E' }}>
                  新疆平安保险有限公司
                </h2>
                <div className="mt-4 space-y-4" style={{ color: '#6B7280' }}>
                  <p>
                    新疆平安保险有限公司成立于2005年，是中国平安保险集团在新疆地区的授权服务机构。
                  </p>
                  <p>
                    多年来，我们始终秉承"专业创造价值"的经营理念，致力于为广大客户提供全方位的保险保障服务。
                  </p>
                  <p>
                    公司业务涵盖车险、财产险、健康险、寿险等多个领域，拥有专业的保险顾问团队和完善的理赔服务体系。
                  </p>
                </div>
                <button
                  onClick={scrollToContact}
                  className="mt-6 px-6 py-3 rounded-lg text-white font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                  style={{ backgroundColor: '#FF6B3D' }}
                  onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#E55A2B'; }}
                  onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = '#FF6B3D'; }}
                >
                  联系我们
                </button>
              </div>

              {/* 右栏 - 图片 */}
              <div>
                <img
                  src={aboutImages[0]}
                  alt="办公环境"
                  className="rounded-xl shadow-lg w-full aspect-[4/3] object-cover"
                />
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <img src={aboutImages[1]} alt="团队风采" className="rounded-lg aspect-video object-cover" />
                  <img src={aboutImages[2]} alt="团队风采" className="rounded-lg aspect-video object-cover" />
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ========== Section 5: 联系方式与在线表单 ========== */}
      <section ref={contactRef} style={{ backgroundColor: '#F7F8FA' }} className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal className="text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: '#FFF5F2', color: '#FF6B3D' }}>
              <Phone className="w-4 h-4" />
              联系我们
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4" style={{ color: '#1A1A2E' }}>
              期待与您联系
            </h2>
          </ScrollReveal>

          <ScrollReveal delay={100}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-12">
              {/* 左栏 - 联系方式 */}
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-xl font-bold" style={{ color: '#1A1A2E' }}>新疆平安保险有限公司</h3>
                <div className="border-t border-gray-200 my-6" />
                <div className="space-y-5">
                  {contactInfo.map((c, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: '#FFF5F2' }}
                      >
                        <c.Icon className="w-5 h-5" style={{ color: '#FF6B3D' }} />
                      </div>
                      <div>
                        <p className="text-sm" style={{ color: '#9CA3AF' }}>{c.label}</p>
                        <p className="text-base font-medium" style={{ color: '#1A1A2E' }}>{c.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 右栏 - 留言表单 */}
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <h3 className="text-lg font-semibold mb-6" style={{ color: '#1A1A2E' }}>在线留言</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <input
                      type="text"
                      placeholder="请输入您的姓名"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 text-sm transition-all"
                      style={{ '--tw-ring-color': '#FF6B3D' } as React.CSSProperties}
                      onFocus={(e) => { e.currentTarget.style.borderColor = '#FF6B3D'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,107,61,0.1)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; }}
                    />
                    {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
                  </div>
                  <div>
                    <input
                      type="tel"
                      placeholder="请输入您的手机号"
                      value={formData.phone}
                      onChange={(e) => handleFormChange('phone', e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none text-sm transition-all"
                      onFocus={(e) => { e.currentTarget.style.borderColor = '#FF6B3D'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,107,61,0.1)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; }}
                    />
                    {formErrors.phone && <p className="text-xs text-red-500 mt-1">{formErrors.phone}</p>}
                  </div>
                  <div>
                    <select
                      value={formData.insuranceType}
                      onChange={(e) => handleFormChange('insuranceType', e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none text-sm transition-all bg-white"
                      onFocus={(e) => { e.currentTarget.style.borderColor = '#FF6B3D'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,107,61,0.1)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <option value="">请选择意向险种</option>
                      <option value="chexian">车险</option>
                      <option value="jiankang">健康险</option>
                      <option value="jiacai">家财险</option>
                      <option value="yiwaixian">意外险</option>
                      <option value="shouxian">寿险</option>
                      <option value="lüyou">旅游险</option>
                      <option value="qita">其他</option>
                    </select>
                    {formErrors.insuranceType && <p className="text-xs text-red-500 mt-1">{formErrors.insuranceType}</p>}
                  </div>
                  <div>
                    <textarea
                      rows={4}
                      placeholder="请描述您的保险需求..."
                      value={formData.message}
                      onChange={(e) => handleFormChange('message', e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none text-sm resize-none transition-all"
                      onFocus={(e) => { e.currentTarget.style.borderColor = '#FF6B3D'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,107,61,0.1)'; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.boxShadow = 'none'; }}
                    />
                    {formErrors.message && <p className="text-xs text-red-500 mt-1">{formErrors.message}</p>}
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 rounded-lg text-white font-semibold transition-all duration-300 hover:shadow-lg disabled:opacity-60"
                    style={{ backgroundColor: '#FF6B3D' }}
                    onMouseEnter={(e) => { if (!submitting) (e.target as HTMLElement).style.backgroundColor = '#E55A2B'; }}
                    onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = '#FF6B3D'; }}
                  >
                    {submitting ? '提交中...' : '提交留言'}
                  </button>
                  {showSuccess && (
                    <div className="mt-4 p-4 rounded-lg flex items-center gap-2" style={{ backgroundColor: '#ECFDF5', color: '#059669' }}>
                      <Check className="w-5 h-5 flex-shrink-0" />
                      <span className="text-sm font-medium">提交成功！我们会尽快与您联系。</span>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 底部间距（为悬浮条留空间） */}
      <div className="pb-20" />

      {/* ========== 底部悬浮条 ========== */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="hidden sm:block">
            <p className="text-sm font-medium" style={{ color: '#1A1A2E' }}>有疑问？立即咨询专业顾问</p>
            <p className="text-xs" style={{ color: '#9CA3AF' }}>7x24小时在线服务</p>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <a
              href="tel:0991-XXXXXXX"
              className="flex items-center gap-2 px-4 md:px-5 py-2 rounded-lg border font-medium text-sm transition-all duration-300"
              style={{ borderColor: '#FF6B3D', color: '#FF6B3D' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#FFF5F2'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">拨打电话</span>
            </a>
            <button
              onClick={scrollToContact}
              className="flex items-center gap-2 px-4 md:px-5 py-2 rounded-lg text-white font-medium text-sm transition-all duration-300 hover:shadow-md"
              style={{ backgroundColor: '#FF6B3D' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E55A2B'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FF6B3D'; }}
            >
              <MessageCircle className="w-4 h-4" />
              <span className="hidden sm:inline">免费咨询</span>
            </button>
          </div>
        </div>
      </div>

      {/* ========== 产品详情模态框 ========== */}
      {modalProduct && (
        <ProductDetailModal
          product={modalProduct}
          onClose={() => setModalProduct(null)}
          onConsult={scrollToContact}
        />
      )}

      {/* ========== 页脚 ========== */}
      <PingAnFooter />
    </div>
  );
}
